import { useEffect, useMemo, useRef } from 'react';
import {
  Animated, NativeScrollEvent, NativeSyntheticEvent, PanResponder, PanResponderInstance,
} from 'react-native';

/**
 * Reusable swipe-to-dismiss for bottom-sheet style modals — picker
 * lists, NumericKeypad, info sheets. The host wraps the sheet in an
 * `Animated.View`, applies `transform: [{ translateY }]`, and spreads
 * `panHandlers` so the gesture starts on any touch inside the sheet.
 *
 * A sheet with a scrollable list inside spreads `listProps` on it as
 * well. Taking the gesture costs the list its own: on Android a claimed
 * JS responder stops the native ScrollView receiving the rest of the
 * touch, and since the sheet only follows a finger moving down, a list
 * that has been taken over looks dead rather than scrolled. So the sheet
 * only takes the gesture while the list is at its top, where there is
 * nothing above to scroll to and pulling down can only mean dismiss.
 *
 * Behaviour:
 *   - The gesture is only captured once the finger has moved DOWN by
 *     a few px and the movement is mostly vertical — that way taps on
 *     in-sheet rows / buttons still go through without being swallowed
 *     by the pan responder.
 *   - The sheet follows the finger only downward; upward drag is
 *     pinned at the resting position.
 *   - On release, the sheet either closes (with a 180 ms slide-out
 *     past the threshold or with enough downward velocity) or springs
 *     back to its resting position.
 *   - On the sheet becoming visible again (`visible` flag flips
 *     false → true), the translation resets to 0 so the next open
 *     starts clean.
 *
 * Numbers chosen to feel close to the iOS system sheet dismiss; tune
 * via the options arg if a particular screen wants stricter / looser
 * behaviour.
 */
export interface UseSwipeDownDismissOptions {
  /** Pixels of downward drag that triggers close. Default 80. */
  closeThreshold?: number;
  /** Velocity (dy/ms) that triggers close regardless of distance. Default 0.5. */
  closeVelocity?: number;
  /** Pixels of movement before the gesture grabs the responder. Default 8. */
  activationDelta?: number;
  /** Off-screen target Y used by the close animation. Default 600. */
  exitDistance?: number;
  /** Close animation duration in ms. Default 180. */
  closeDuration?: number;
  /** Slide-in animation duration in ms when sheet becomes visible.
   *  Default 220 — feels close to iOS system modal slide-up. Set to 0
   *  for an instant appearance (matches pre-animation legacy behaviour). */
  openDuration?: number;
}

export interface UseSwipeDownDismissResult {
  /** Animated.Value driving the sheet's vertical offset. */
  translateY: Animated.Value;
  /** Pan handlers — spread onto the sheet's Animated.View. */
  panHandlers: PanResponderInstance['panHandlers'];
  /**
   * Spread onto a ScrollView inside the sheet, so the drag waits until
   * the list is at its top. A sheet with no list spreads nothing and
   * drags from anywhere, as before.
   */
  listProps: {
    onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
    scrollEventThrottle: number;
  };
}

/**
 * Whether the sheet takes over the drag. Kept out of the PanResponder,
 * which is a closure a test cannot reach into.
 *
 * Down, mostly vertical, far enough to be meant — and only while the
 * list behind the finger is at its top.
 */
export const shouldDragSheet = (
  gesture: { dx: number; dy: number },
  options: { activationDelta: number; listAtTop: boolean },
): boolean =>
  options.listAtTop
  && gesture.dy > options.activationDelta
  && Math.abs(gesture.dy) > Math.abs(gesture.dx);

export const useSwipeDownDismiss = (
  visible: boolean,
  onClose: () => void,
  options: UseSwipeDownDismissOptions = {},
): UseSwipeDownDismissResult => {
  const {
    closeThreshold = 80,
    closeVelocity = 0.5,
    activationDelta = 8,
    exitDistance = 600,
    closeDuration = 180,
    openDuration = 220,
  } = options;

  // Инициализируем на exitDistance (offscreen) чтобы первый рендер сразу
  // после mount'а показывал sheet за нижней кромкой экрана — тогда
  // slide-in анимация ниже пойдёт от offscreen к rest'у, как Modal
  // animationType="slide" делает out-of-the-box.
  const translateY = useRef(new Animated.Value(exitDistance)).current;

  // Whether the sheet's list is scrolled to its top. True for a sheet
  // without one, which is every sheet that never spreads `listProps`.
  const listAtTop = useRef(true);

  // На каждый visible=true запускаем последовательность:
  //   1. Snap на exitDistance (offscreen) — sync с native driver'ом
  //      после возможной прошлой close-анимации.
  //   2. Slide-in с openDuration до 0 (rest position).
  //
  // Sequence используется потому что setValue с useNativeDriver: true
  // не всегда пробрасывает JS-side value в native (известная quirk RN);
  // zero-duration timing — единственный надёжный reset. Если
  // openDuration=0, sheet появится мгновенно как раньше — вариант для
  // хостов, которые не хотят анимации.
  useEffect(() => {
    if (visible) {
      listAtTop.current = true;
      Animated.sequence([
        Animated.timing(translateY, {
          toValue: exitDistance,
          duration: 0,
          useNativeDriver: true,
        }),
        Animated.timing(translateY, {
          toValue: 0,
          duration: openDuration,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible, translateY, exitDistance, openDuration]);

  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onMoveShouldSetPanResponder: (_, g) =>
          shouldDragSheet(g, { activationDelta, listAtTop: listAtTop.current }),
        onPanResponderMove: (_, g) => {
          if (g.dy > 0) {
            translateY.setValue(g.dy);
          }
        },
        onPanResponderRelease: (_, g) => {
          const shouldClose = g.dy > closeThreshold || g.vy > closeVelocity;
          if (shouldClose) {
            Animated.timing(translateY, {
              toValue: exitDistance,
              duration: closeDuration,
              useNativeDriver: true,
            }).start(({ finished }) => {
              if (finished) {
                onClose();
              }
            });
          } else {
            Animated.spring(translateY, {
              toValue: 0,
              useNativeDriver: true,
              speed: 18,
              bounciness: 4,
            }).start();
          }
        },
        onPanResponderTerminate: () => {
          Animated.spring(translateY, {
            toValue: 0,
            useNativeDriver: true,
          }).start();
        },
      }),
    [
      onClose,
      translateY,
      activationDelta,
      closeThreshold,
      closeVelocity,
      exitDistance,
      closeDuration,
    ],
  );

  const listProps = useMemo(
    () => ({
      onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        listAtTop.current = event.nativeEvent.contentOffset.y <= 0;
      },
      scrollEventThrottle: 16,
    }),
    [],
  );

  return { translateY, panHandlers: panResponder.panHandlers, listProps };
};
